/**
 * compass-core.js — an honest, tilt-compensated compass heading.
 *
 * WHY THIS EXISTS (and why every web compass we could find is wrong)
 * -----------------------------------------------------------------
 * The web is full of pages that do this:
 *
 *      heading = 360 - event.alpha
 *
 * That single line carries four separate defects, and this module exists to
 * fix each one:
 *
 * 1. IT MAY NOT BE A COMPASS AT ALL. Plain `deviceorientation.alpha` on Android
 *    is RELATIVE — zeroed wherever the page happened to load. It looks like a
 *    compass, it moves like a compass, and it points at nothing. The absolute
 *    stream is a different event (`deviceorientationabsolute`), and when neither
 *    is absolute the only correct answer is to say so.
 *
 * 2. IT IGNORES TILT. alpha is the yaw of the DEVICE, so it only equals the
 *    direction "the phone points" while the phone is flat. Raise it to eye
 *    level to sight a landmark — the natural way to use a compass — and the
 *    top-edge axis rotates toward vertical, where its heading is undefined:
 *    at 90° of pitch alpha spins freely with the tiniest roll. We solve the
 *    real problem: build the rotation matrix, then take the heading of the
 *    axis the user is actually pointing (top edge when flat, camera axis when
 *    upright), switching with hysteresis so it cannot flap.
 *
 * 3. IT IGNORES SCREEN ROTATION. Turn the phone to landscape and the device's
 *    +Y axis is no longer the top of the screen, so the needle is 90° off while
 *    looking perfectly plausible.
 *
 * 4. IT REPORTS MAGNETIC NORTH AS "NORTH". See geomag-wmm.js — that is a
 *    separate lie worth up to 30°, and this module keeps magnetic and true
 *    strictly apart so the page can never print one under the other's label.
 *
 * On iOS none of the above is available directly: Safari's alpha is arbitrary
 * and the only real reading is `webkitCompassHeading`, a single flat-plane
 * number. We recover the full 3-D behaviour anyway by continuously estimating
 * the yaw offset between Safari's arbitrary alpha and its true compass reading
 * — a constant rotation about the world's vertical axis — and then using the
 * matrix as on Android. That gives iOS users a working sighting mode, which as
 * far as we can tell no web compass has.
 *
 * ACCURACY IS REPORTED, NOT ASSUMED
 * ---------------------------------
 * A compass that is 30° off next to a car door looks exactly like a compass
 * that is right. So we measure our own dispersion: while the device is still
 * (accelerometer magnitude ≈ g with low variance), the circular standard
 * deviation of the heading IS the noise floor, and a still device whose needle
 * wanders is a device in a disturbed field. iOS additionally hands us
 * `webkitCompassAccuracy` and we prefer it when it is valid.
 *
 * Presentation-agnostic: touches no DOM. Pure math is exported for Node tests
 * (`tests/compass-core.test.mjs`), which is where the axis/screen/tilt conventions
 * are pinned down against hand-computed cases.
 */
(function (root, factory) {
    var CompassCore = factory();
    if (typeof window !== 'undefined') window.CompassCore = CompassCore;
    if (typeof module !== 'undefined' && module.exports) module.exports = CompassCore;
}(this, function () {
    'use strict';

    var DEG = Math.PI / 180, RAD = 180 / Math.PI;

    // ── Pure geometry ────────────────────────────────────────────────────────

    function norm360(d) { d = d % 360; return d < 0 ? d + 360 : d; }

    /** Signed shortest angular difference a − b, in (−180, 180]. */
    function angleDiff(a, b) {
        var d = norm360(a - b);
        return d > 180 ? d - 360 : d;
    }

    /**
     * W3C device→earth rotation matrix for the intrinsic Z-X'-Y'' Tait-Bryan
     * angles the DeviceOrientation API reports. Earth frame is ENU:
     * x = east, y = north, z = up. Row-major 3×3 as a flat array.
     */
    function rotationMatrix(alphaDeg, betaDeg, gammaDeg) {
        var a = (alphaDeg || 0) * DEG, b = (betaDeg || 0) * DEG, g = (gammaDeg || 0) * DEG;
        var cA = Math.cos(a), sA = Math.sin(a);
        var cB = Math.cos(b), sB = Math.sin(b);
        var cG = Math.cos(g), sG = Math.sin(g);
        return [
            cA * cG - sA * sB * sG, -sA * cB, cA * sG + sA * sB * cG,
            sA * cG + cA * sB * sG, cA * cB, sA * sG - cA * sB * cG,
            -cB * sG, sB, cB * cG
        ];
    }

    /** Earth-frame direction (ENU) of a device-frame unit vector. */
    function toEarth(R, v) {
        return [
            R[0] * v[0] + R[1] * v[1] + R[2] * v[2],
            R[3] * v[0] + R[4] * v[1] + R[5] * v[2],
            R[6] * v[0] + R[7] * v[1] + R[8] * v[2]
        ];
    }

    /** Compass heading (0 = north, clockwise) of an ENU vector; null if vertical. */
    function headingOf(enu) {
        var horiz = Math.sqrt(enu[0] * enu[0] + enu[1] * enu[1]);
        if (horiz < 1e-6) return null;             // pointing at the sky/ground
        return norm360(Math.atan2(enu[0], enu[1]) * RAD);
    }

    /** Elevation above the horizon, in degrees, of an ENU vector. */
    function elevationOf(enu) {
        var m = Math.sqrt(enu[0] * enu[0] + enu[1] * enu[1] + enu[2] * enu[2]) || 1;
        return Math.asin(Math.max(-1, Math.min(1, enu[2] / m))) * RAD;
    }

    /**
     * The device-frame axis that corresponds to "the top of the screen as the
     * user sees it", given screen.orientation.angle. In portrait that is +Y;
     * rotate the phone into landscape and it becomes ±X. Missing this is why a
     * landscape compass reads 90° off.
     */
    function screenTopAxis(screenAngleDeg) {
        var a = (screenAngleDeg || 0) * DEG;
        return [Math.sin(a), Math.cos(a), 0];
    }

    /**
     * Headings of both usable axes plus the geometry needed to choose between
     * them. `flat` is the top-of-screen axis (phone held level, map style);
     * `sight` is the camera axis (phone held upright, rifle-sight style).
     */
    function axisHeadings(alpha, beta, gamma, screenAngleDeg) {
        var R = rotationMatrix(alpha, beta, gamma);
        var topE = toEarth(R, screenTopAxis(screenAngleDeg));
        var backE = toEarth(R, [0, 0, -1]);      // out of the back of the phone
        return {
            flat: headingOf(topE),
            sight: headingOf(backE),
            topElevation: elevationOf(topE),      // ≈0 flat, ≈+90 held upright
            backElevation: elevationOf(backE),
            R: R
        };
    }

    /**
     * Which axis to trust. Hysteresis on purpose: a single threshold makes the
     * needle jump between two different headings while the user's hand shakes
     * around it, which reads as a broken compass.
     */
    var SIGHT_ENTER = 55, SIGHT_EXIT = 40;
    function chooseAxis(topElevationDeg, current) {
        var e = Math.abs(topElevationDeg);
        if (current === 'sight') return e > SIGHT_EXIT ? 'sight' : 'flat';
        return e > SIGHT_ENTER ? 'sight' : 'flat';
    }

    // ── Circular statistics (a heading of 359° and one of 1° are 2° apart) ────

    function circularMean(list) {
        if (!list || !list.length) return null;
        var s = 0, c = 0;
        for (var i = 0; i < list.length; i++) { s += Math.sin(list[i] * DEG); c += Math.cos(list[i] * DEG); }
        if (Math.abs(s) < 1e-12 && Math.abs(c) < 1e-12) return null;
        return norm360(Math.atan2(s, c) * RAD);
    }

    /** Circular standard deviation in degrees (Mardia). 0 = perfectly steady. */
    function circularStd(list) {
        if (!list || list.length < 2) return 0;
        var s = 0, c = 0;
        for (var i = 0; i < list.length; i++) { s += Math.sin(list[i] * DEG); c += Math.cos(list[i] * DEG); }
        var Rbar = Math.sqrt(s * s + c * c) / list.length;
        if (Rbar >= 1) return 0;
        if (Rbar <= 1e-9) return 180;
        return Math.sqrt(-2 * Math.log(Rbar)) * RAD;
    }

    /** Angular low-pass that crosses 0/360 correctly. */
    function smoothAngle(prev, next, k) {
        if (prev == null || !isFinite(prev)) return next;
        if (next == null || !isFinite(next)) return prev;
        return norm360(prev + angleDiff(next, prev) * k);
    }

    /** Cardinal/intercardinal index (0..15) for a heading — N, NNE, NE, … */
    function pointIndex(heading) {
        return Math.round(norm360(heading) / 22.5) % 16;
    }

    /** Back azimuth — the way home. */
    function backAzimuth(heading) { return norm360(heading + 180); }

    /**
     * Great-circle bearing and distance between two WGS84 points.
     * Distance by haversine on the mean Earth radius: 0.3% worst case, which is
     * 3 m per km — far below GPS noise, and it needs no iteration.
     */
    function bearingTo(lat1, lon1, lat2, lon2) {
        var p1 = lat1 * DEG, p2 = lat2 * DEG, dl = (lon2 - lon1) * DEG;
        var y = Math.sin(dl) * Math.cos(p2);
        var x = Math.cos(p1) * Math.sin(p2) - Math.sin(p1) * Math.cos(p2) * Math.cos(dl);
        return norm360(Math.atan2(y, x) * RAD);
    }
    function distanceTo(lat1, lon1, lat2, lon2) {
        var Rm = 6371008.8;
        var p1 = lat1 * DEG, p2 = lat2 * DEG;
        var dp = (lat2 - lat1) * DEG, dl = (lon2 - lon1) * DEG;
        var a = Math.sin(dp / 2) * Math.sin(dp / 2) +
            Math.cos(p1) * Math.cos(p2) * Math.sin(dl / 2) * Math.sin(dl / 2);
        return 2 * Rm * Math.asin(Math.min(1, Math.sqrt(a)));
    }

    // ── Live engine ──────────────────────────────────────────────────────────

    function CompassCore(opts) {
        opts = opts || {};
        this.smooth = (opts.smooth == null) ? 0.25 : opts.smooth;   // needle damping
        this.stillWindowMs = opts.stillWindowMs || 2500;            // accuracy window
        this.declination = null;                                    // set by the page from GPS

        this.state = {
            ready: false,
            source: null,        // 'ios' | 'absolute' | 'relative' | null
            absolute: false,     // is the yaw tied to the Earth at all?
            headingMagnetic: null,
            headingTrue: null,
            raw: null,           // unsmoothed, for statistics
            axis: 'flat',
            topElevation: 0,
            tiltDeg: 0,          // how far from level, for the "hold flat" cue
            accuracyDeg: null,   // ± degrees, honest
            accuracySource: null,// 'ios' | 'stillness' | null
            still: false,
            interference: false,
            samples: 0
        };

        this._alpha = 0; this._beta = 0; this._gamma = 0;
        this._iosHeading = null; this._iosAccuracy = null;
        this._yawOffset = null;      // iOS: matrix yaw → true magnetic yaw
        this._hist = [];             // {t, heading} for the stillness window
        this._stillSince = 0;
        this._accel = null;
        this._listening = false;
        this._onUpdate = null;
        this._absTimer = 0;
        this._sawAbsolute = false;
        // What the browser delivered, for the «no sensor» verdict: Chromium
        // answers a device without orientation sensors with events whose
        // angles are all null; motion events still come if there is an
        // accelerometer. Nothing at all means the browser passes no sensors.
        this.evidence = { orientNull: 0, motion: 0 };

        this._boundAbs = this._onOrientation.bind(this, true);
        this._boundRel = this._onOrientation.bind(this, false);
        this._boundMotion = this._onMotion.bind(this);
    }

    CompassCore.prototype.onUpdate = function (cb) { this._onUpdate = cb; return this; };

    /** Feed the local magnetic declination (degrees east) so true north exists. */
    CompassCore.prototype.setDeclination = function (deg) {
        this.declination = (typeof deg === 'number' && isFinite(deg)) ? deg : null;
        this._recomputeTrue();
    };

    CompassCore.prototype._recomputeTrue = function () {
        var s = this.state;
        s.headingTrue = (s.headingMagnetic != null && this.declination != null)
            ? norm360(s.headingMagnetic + this.declination) : null;
    };

    /**
     * iOS 13+ gates the sensor behind a permission prompt that MUST be called
     * from a user gesture. Everything else attaches immediately.
     */
    CompassCore.prototype.start = function () {
        var self = this;
        if (typeof window === 'undefined' || !('DeviceOrientationEvent' in window)) {
            return Promise.resolve({ permission: 'unsupported' });
        }
        var DOE = window.DeviceOrientationEvent;
        var chain = Promise.resolve('granted');
        if (typeof DOE.requestPermission === 'function') {
            chain = DOE.requestPermission().catch(function (e) { return { __err: String(e && e.message || e) }; });
        }
        return chain.then(function (res) {
            if (res && res.__err) return { permission: 'error', error: res.__err };
            if (res !== 'granted' && res !== undefined) return { permission: 'denied' };
            self._attach();
            return { permission: 'granted' };
        });
    };

    CompassCore.prototype._attach = function () {
        if (this._listening) return;
        this._listening = true;
        // Absolute first: on Chrome/Android `deviceorientation` is relative and
        // `deviceorientationabsolute` is the real compass. Both are attached
        // because Safari fires only the former, and the absolute one wins
        // whenever it actually delivers.
        if ('ondeviceorientationabsolute' in window) {
            window.addEventListener('deviceorientationabsolute', this._boundAbs, { passive: true });
        }
        window.addEventListener('deviceorientation', this._boundRel, { passive: true });
        window.addEventListener('devicemotion', this._boundMotion, { passive: true });
    };

    CompassCore.prototype.stop = function () {
        if (!this._listening) return;
        this._listening = false;
        try {
            window.removeEventListener('deviceorientationabsolute', this._boundAbs);
            window.removeEventListener('deviceorientation', this._boundRel);
            window.removeEventListener('devicemotion', this._boundMotion);
        } catch (e) {}
    };

    CompassCore.prototype._onMotion = function (e) {
        var a = e.accelerationIncludingGravity;
        if (!a || typeof a.x !== 'number') return;
        this.evidence.motion++;
        this._accel = Math.sqrt(a.x * a.x + a.y * a.y + a.z * a.z);
    };

    CompassCore.prototype._screenAngle = function () {
        try {
            if (window.screen && window.screen.orientation && typeof window.screen.orientation.angle === 'number') {
                return window.screen.orientation.angle;
            }
            if (typeof window.orientation === 'number') return window.orientation;
        } catch (e) {}
        return 0;
    };

    CompassCore.prototype._onOrientation = function (fromAbsolute, e) {
        if (e.alpha == null && e.webkitCompassHeading == null) { this.evidence.orientNull++; return; }
        if (fromAbsolute) this._sawAbsolute = true;
        // Once the absolute event has ever fired, ignore the relative one: its
        // alpha is a different (arbitrary) zero and mixing them makes the
        // needle stutter between two frames of reference.
        if (!fromAbsolute && this._sawAbsolute) return;

        this._alpha = e.alpha || 0;
        this._beta = e.beta || 0;
        this._gamma = e.gamma || 0;

        var s = this.state;
        if (typeof e.webkitCompassHeading === 'number' && isFinite(e.webkitCompassHeading)) {
            this._iosHeading = e.webkitCompassHeading;
            this._iosAccuracy = (typeof e.webkitCompassAccuracy === 'number') ? e.webkitCompassAccuracy : null;
            s.source = 'ios'; s.absolute = true;
        } else if (fromAbsolute || e.absolute === true) {
            s.source = 'absolute'; s.absolute = true;
        } else if (!s.absolute) {
            s.source = 'relative'; s.absolute = false;
        }

        this._compute();
    };

    CompassCore.prototype._compute = function () {
        var s = this.state;
        var ax = axisHeadings(this._alpha, this._beta, this._gamma, this._screenAngle());

        // The W3C matrix yaw runs opposite to a compass heading: a device whose
        // top edge points north has alpha 0, and our matrix already returns 0
        // there, so no extra 360− dance. (Pinned by tests/compass-core.test.mjs.)
        var flat = ax.flat, sight = ax.sight;

        if (s.source === 'ios') {
            // Recover the constant yaw offset between Safari's arbitrary alpha
            // and its real compass reading. Only trust samples taken while the
            // phone is near level — that is the plane webkitCompassHeading is
            // defined in — then keep it as a slowly-tracked constant so it also
            // serves the upright sighting axis.
            if (this._iosHeading != null && flat != null && Math.abs(ax.topElevation) < 40) {
                var off = angleDiff(this._iosHeading, flat);
                this._yawOffset = (this._yawOffset == null) ? off
                    : this._yawOffset + angleDiff(off, this._yawOffset) * 0.15;
            }
            if (this._yawOffset != null) {
                if (flat != null) flat = norm360(flat + this._yawOffset);
                if (sight != null) sight = norm360(sight + this._yawOffset);
            } else if (this._iosHeading != null) {
                flat = this._iosHeading;   // before the offset settles, use what we have
            }
        }

        s.axis = chooseAxis(ax.topElevation, s.axis);
        s.topElevation = ax.topElevation;
        // Distance from perfectly level, for the "hold it flat" bubble.
        s.tiltDeg = Math.abs(ax.topElevation);

        var raw = (s.axis === 'sight') ? sight : flat;
        if (raw == null) raw = (flat != null) ? flat : sight;
        if (raw == null) return;

        s.raw = raw;
        s.headingMagnetic = smoothAngle(s.headingMagnetic, raw, this.smooth);
        this._recomputeTrue();
        s.samples++;
        s.ready = true;

        this._updateAccuracy(raw);

        if (this._onUpdate) this._onUpdate(s);
    };

    /**
     * Honest accuracy. Preference order:
     *   1. iOS's own estimate when valid (it knows its calibration state);
     *   2. our measured dispersion while the device is still — a still phone
     *      whose heading wanders is sitting in a disturbed field, and that is
     *      exactly the situation users never get told about.
     */
    CompassCore.prototype._updateAccuracy = function (raw) {
        var s = this.state;
        var now = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();

        var still = (this._accel == null) ? false : Math.abs(this._accel - 9.81) < 0.6;
        if (!still) { this._hist.length = 0; this._stillSince = 0; s.still = false; }
        else {
            if (!this._stillSince) this._stillSince = now;
            s.still = true;
            this._hist.push({ t: now, h: raw });
            while (this._hist.length && now - this._hist[0].t > this.stillWindowMs) this._hist.shift();
        }

        if (this._iosAccuracy != null && this._iosAccuracy >= 0) {
            s.accuracyDeg = this._iosAccuracy;
            s.accuracySource = 'ios';
        } else if (s.still && this._hist.length >= 12) {
            var arr = [];
            for (var i = 0; i < this._hist.length; i++) arr.push(this._hist[i].h);
            var sd = circularStd(arr);
            // 2σ ≈ 95% of readings; floor at 1° so we never advertise a
            // precision the magnetometer hardware cannot deliver.
            s.accuracyDeg = Math.max(1, Math.round(sd * 2));
            s.accuracySource = 'stillness';
        }
        // A still phone with >8° of wander is not noise, it is iron.
        s.interference = !!(s.still && s.accuracyDeg != null && s.accuracyDeg > 8);
    };

    /**
     * The whole device orientation, not one heading: the device→Earth (ENU)
     * matrix with its yaw tied to north — true north once a declination is
     * set (pass `false` for magnetic), and on iOS through the recovered yaw
     * offset. For pages that project the sky onto the camera (AR); null while
     * the yaw is not tied to the Earth (relative stream, iOS offset not settled).
     */
    CompassCore.prototype.earthMatrix = function (useTrue) {
        var s = this.state;
        if (!s.ready || !s.absolute) return null;
        var R = rotationMatrix(this._alpha, this._beta, this._gamma);
        var yaw = 0;
        if (s.source === 'ios') {
            if (this._yawOffset == null) return null;
            yaw = this._yawOffset;
        }
        if (useTrue !== false && this.declination != null) yaw += this.declination;
        if (!yaw) return R;
        // Rotate about «up» so that every heading grows by `yaw`, clockwise.
        var t = yaw * DEG, c = Math.cos(t), sn = Math.sin(t);
        return [
            c * R[0] + sn * R[3], c * R[1] + sn * R[4], c * R[2] + sn * R[5],
            -sn * R[0] + c * R[3], -sn * R[1] + c * R[4], -sn * R[2] + c * R[5],
            R[6], R[7], R[8]
        ];
    };

    /** Test hook: drive the engine without hardware. */
    CompassCore.prototype._feed = function (o) {
        this._onOrientation(!!o.absolute, {
            alpha: o.alpha, beta: o.beta, gamma: o.gamma,
            absolute: !!o.absolute,
            webkitCompassHeading: o.webkitCompassHeading,
            webkitCompassAccuracy: o.webkitCompassAccuracy
        });
    };
    CompassCore.prototype._feedMotion = function (mag) { this._accel = mag; };

    // Pure exports for the unit tests and for pages that need the geometry
    // without a live sensor (the sun-alignment fallback uses bearingTo).
    CompassCore.norm360 = norm360;
    CompassCore.angleDiff = angleDiff;
    CompassCore.rotationMatrix = rotationMatrix;
    CompassCore.toEarth = toEarth;
    CompassCore.headingOf = headingOf;
    CompassCore.elevationOf = elevationOf;
    CompassCore.screenTopAxis = screenTopAxis;
    CompassCore.axisHeadings = axisHeadings;
    CompassCore.chooseAxis = chooseAxis;
    CompassCore.circularMean = circularMean;
    CompassCore.circularStd = circularStd;
    CompassCore.smoothAngle = smoothAngle;
    CompassCore.pointIndex = pointIndex;
    CompassCore.backAzimuth = backAzimuth;
    CompassCore.bearingTo = bearingTo;
    CompassCore.distanceTo = distanceTo;

    return CompassCore;
}));
